import { DSSpacer } from '@zoe/ds-web'
import {
  DSAccordion,
  DSFlex,
  DSHeroCarousel,
  DSIcon,
  type DSIconName,
  DSImage,
  DSRating,
  DSText,
} from '@zoe/ds-web/next-gen'
import type { ComponentProps, FC, ReactNode } from 'react'
import styled from 'styled-components'

import { AvatarGroup } from '@/components/ds/AvatarGroup/AvatarGroup'
import { BREAKPOINTS } from '@/components/ds/breakpoints'
import { type BreakConfigProps, DSGrid, DSGridBlock } from '@/components/ds/Grid/Grid'
import type { ContentImage } from '@/components/patterns/content'

import useBreakpoints from '@/lib/hooks/useBreakpoints'

export interface PdpHeroFeature {
  icon: DSIconName
  label: string
}

export interface PdpHeroDetail {
  title: string
  body: string
}

export interface PdpHeroSocialProof {
  avatars: ContentImage[]
  text: string
}

/** The PDP hero, as plain data — so any source can produce it. */
export interface PdpHeroConfig {
  /** The swiper slides — the lead tile plays them as a carousel on every breakpoint. Lead image first. */
  images: ContentImage[]
  /** Static tiles under the lead carousel on desktop; appended to the swiper when stacked. */
  bentoImages?: ContentImage[]
  socialProof?: PdpHeroSocialProof
  title: string
  /** Bold USP line between the title and the description. */
  highlight?: string
  description?: string
  rating?: { value: number; label: string }
  /** Icon + label row under the payment slot (shipping, results timing…). */
  features?: PdpHeroFeature[]
  /** Collapsed product-detail rows; one open at a time. */
  details?: PdpHeroDetail[]
}

/** Two halves from `l` (6 + 6), stacked below. */
const HALF: BreakConfigProps = { s: { colSpan: 2 }, m: { colSpan: 6 }, l: { colSpan: 6 }, xl: { colSpan: 6 } }

/** The copy column's gutter towards the gallery, only once the halves pair (from `l`). */
const CopyColumn = styled.div`
  @media (min-width: ${BREAKPOINTS.l}) {
    padding-left: var(--grid-32);
  }
`

/** Lead image spans both columns; pairs share a row; an odd last image spans again. */
const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--grid-16);

  > :first-child,
  > :nth-child(even):last-child {
    grid-column: span 2;
  }
`

/** Payload mirrors `DSHeroCarousel` — fires when the mobile gallery swipes. */
export type PdpHeroSlideChange = NonNullable<ComponentProps<typeof DSHeroCarousel>['onSlideChange']>

interface PdpHeroProps {
  config: PdpHeroConfig
  /** The price + CTAs (or a full payment/plan section), composed by the caller — the pattern only places it. */
  paymentSlot?: ReactNode
  onSlideChange?: PdpHeroSlideChange
  /** Called on every detail-row toggle with the state being moved to — tracking goes here. */
  onDetailToggle?: (title: string, index: number, isExpanded: boolean) => void
  testId?: string
}

export const PdpHero: FC<PdpHeroProps> = ({ config, paymentSlot, onSlideChange, onDetailToggle, testId }) => {
  const {
    images,
    bentoImages = [],
    socialProof,
    title,
    highlight,
    description,
    rating,
    features = [],
    details = [],
  } = config
  const { breakpoint } = useBreakpoints()
  /** The whole gallery collapses into one swiper whenever the two hero columns are stacked (the hook's `xl` starts at the grid's `l`). */
  const isStacked = breakpoint !== 'xl'
  const stackedSlides = [...images, ...bentoImages]
  const [lead] = images

  return (
    <section data-testid={testId}>
      <DSSpacer direction="vertical" size={24} />
      <DSGrid backgroundColor="white" mobileBackgroundColor="white">
        {/* Unpadded: the gallery spans its half fully; the copy column's own padding separates them. */}
        <DSGridBlock breakConfig={HALF}>
          {isStacked ? (
            stackedSlides.length > 1 ? (
              <DSHeroCarousel
                items={stackedSlides}
                ariaLabel="Product photos"
                aspectRatio="1 / 1"
                loop
                onSlideChange={onSlideChange}
              />
            ) : (
              lead && <DSImage src={lead.src} alt={lead.alt} size="fluid" aspectRatio="1 / 1" />
            )
          ) : (
            <GalleryGrid>
              {images.length > 1 ? (
                <DSHeroCarousel
                  items={images}
                  ariaLabel="Product photos"
                  aspectRatio="1 / 1"
                  loop
                  onSlideChange={onSlideChange}
                />
              ) : (
                lead && <DSImage src={lead.src} alt={lead.alt} size="fluid" aspectRatio="1 / 1" />
              )}
              {bentoImages.map((image) => (
                <DSImage key={image.src} src={image.src} alt={image.alt} size="fluid" aspectRatio="1 / 1" />
              ))}
            </GalleryGrid>
          )}
          <DSSpacer direction="vertical" size={0} breakAt={{ l: 24 }} />
        </DSGridBlock>
        <DSGridBlock breakConfig={HALF}>
          <CopyColumn>
            <DSSpacer direction="vertical" size={48} breakAt={{ l: 0 }} />
            <DSFlex direction="column" gap="betweenSections" width="100%">
              <DSFlex direction="column" gap="betweenDifferent" width="100%">
                {socialProof && (
                  <DSFlex direction="row" gap="betweenSimilar" alignItems="center">
                    <AvatarGroup images={socialProof.avatars} />
                    <DSText variant="paragraphSCaption" isBold color="default">
                      {socialProof.text}
                    </DSText>
                  </DSFlex>
                )}
                <DSText variant="titleXL" color="strong" as="h1">
                  {title}
                </DSText>
                {highlight && (
                  <DSText variant="paragraphMDefault" color="strong" isBold>
                    {highlight}
                  </DSText>
                )}
                {description && (
                  <DSText variant="paragraphMDefault" color="strong">
                    {description}
                  </DSText>
                )}
              </DSFlex>
              {rating && <DSRating value={rating.value} label={rating.label} />}
              {paymentSlot}
              {features.length > 0 && (
                <DSFlex direction="row" gap="betweenSections" justifyContent="center" alignItems="center">
                  {features.map((feature) => (
                    <DSFlex key={feature.label} direction="row" gap="betweenSimilar" alignItems="center">
                      <DSIcon name={feature.icon} size="S" color="default" />
                      <DSText variant="paragraphSCaption" color="subtle">
                        {feature.label}
                      </DSText>
                    </DSFlex>
                  ))}
                </DSFlex>
              )}
              {details.length > 0 && (
                <DSAccordion
                  items={details.map((detail) => ({ id: detail.title, title: detail.title, content: detail.body }))}
                  defaultExpandedId={null}
                  onItemToggle={(item, isExpanded) =>
                    onDetailToggle?.(
                      item.title,
                      details.findIndex((detail) => detail.title === item.id),
                      isExpanded,
                    )
                  }
                />
              )}
            </DSFlex>
          </CopyColumn>
        </DSGridBlock>
      </DSGrid>
      <DSSpacer direction="vertical" size={48} />
    </section>
  )
}
